// Keyboard focus that would otherwise fall to <body>.
//
// A Btn that is disabled while its action runs (Save, Find key links, Choose files, Build) loses
// focus the moment it is disabled, and a button that disappears after its action (Accept, the
// column editor's Save) takes the focus with it. Either way a keyboard or screen-reader user is
// sent back to the top of the page. Found by the keyboard walkthrough of 2026-10-04
// (knowledge/tasks/evidence/2026-10-04-a11y-pass/).
//
//   const restore = rememberFocus();          // before the action: remembers document.activeElement
//   await doTheAction();
//   restore(() => someFallbackElement);       // after it: if focus is on <body>, give it back to the
//                                             // remembered element, or else to the fallback
//
// It waits for React to commit (the button is re-enabled a render later) by retrying for a few
// animation frames, and never steals focus that the user has already moved somewhere else.

const FRAMES = 20;

function lost() {
  const el = document.activeElement;
  return !el || el === document.body || el === document.documentElement;
}

function focusable(el) {
  return el && el.isConnected && !el.disabled && typeof el.focus === "function";
}

export function focusWhenReady(getTarget) {
  let n = 0;
  const tick = () => {
    if (!lost()) return;
    const el = typeof getTarget === "function" ? getTarget() : getTarget;
    if (focusable(el)) { el.focus(); return; }
    n += 1;
    if (n < FRAMES) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export function rememberFocus() {
  const before = typeof document !== "undefined" ? document.activeElement : null;
  // Re-evaluated on every frame: the remembered element once it is enabled again; while it is
  // gone or stays disabled (a submit button that waits for new input), the fallback.
  return fallback => focusWhenReady(() => {
    if (before && before !== document.body && focusable(before)) return before;
    const fb = typeof fallback === "function" ? fallback() : fallback;
    return focusable(fb) ? fb : before;
  });
}
